import { z } from "zod";

import { TRISTATE_FILTER_VALUES } from "@/lib/catalog-list-filters";
import {
  CATALOG_ITEM_SORTS,
  CATALOG_SEARCH_TERM_MAX_LENGTH,
  MAX_CATALOG_PAGE_SIZE,
  catalogSearchTermLength,
  CATALOG_SORT_URL_VALUES,
  CATALOG_VIEWS,
} from "@/lib/catalog-list-url-state";

// The browser seam for the Item Catalog list: what the URL may say, and what
// the browser may ask the BFF to fetch. The wire faces for
// `/internal/catalog/*` are a different seam and live on their `endpoint()`
// declarations (`@/server/data/internal-api/catalog`) — see
// `client/docs/architecture.md` L2, "Two seams, two homes for a schema".
//
// The vocabulary itself is neither seam's: both this file and the page's
// controls name it, so it lives in `@/lib/catalog-list-url-state` (the list's
// own shape) and `@/lib/catalog-list-filters` (what narrows it), exactly as
// the admin tab slugs live in `@/lib/admin-tabs`.

/**
 * The highest page either face will carry.
 *
 * The catalog is ~11k items and the smallest page size is 10, so ~1,100 pages
 * covers it with room to grow. The point is not to predict the catalog's size
 * but to keep `?page=1e9` from becoming an `OFFSET 10000000000` in Postgres:
 * an offset scan that deep costs the same whether or not any rows come back.
 */
export const MAX_CATALOG_PAGE = 100_000;

/**
 * The width of `catalog_categories.slug`, from the source's `SlugField(50)`.
 * Pinned against Python in `catalog-wire-drift.unit.test.ts`.
 */
export const CATEGORY_SLUG_MAX_LENGTH = 50;

/**
 * A page size this app will fetch: a whole number of rows, up to the ceiling.
 *
 * Both faces below share it, so the URL and the server-function input cannot
 * disagree about what a page may be — the difference between them is what
 * happens to a value that fails it, not what passes.
 *
 * It replaced a union of the four menu literals. That union was the tighter
 * type (its output was `CatalogPageSize`, so no reader needed a cast), but it
 * was the wrong *rule*: it made the menu the bound on every page size, and the
 * donor bounds only the menu (#527).
 */
const catalogPageSize = z.number().int().min(1).max(MAX_CATALOG_PAGE_SIZE);

/**
 * A number, from a param that may carry it either way.
 *
 * TanStack JSON-parses each param, so `?page=3` arrives as a number and a
 * hand-pasted `?page=%223%22` as a string — both mean page 3. The union does
 * that much and no more: bare `z.coerce.number()` also turns `?page=true` into
 * 1 and `?category=[]` into 0, addressing a real page or category from a value
 * that named neither.
 */
const urlNumber = z.union([z.number(), z.string().trim().min(1)]).pipe(z.coerce.number());

/**
 * A page size, read from a URL.
 *
 * A **range**, not the menu's literal union: the donor honours whatever number
 * the address carries (`items-list-page.tsx:175` is
 * `Number(searchParams.get('pageSize')) || 20`, with no membership check), so a
 * link to 37 rows is an address to honour rather than a typo to correct. The
 * menu still offers only `CATALOG_PAGE_SIZES` — this is what a link may say,
 * not a new control. Bounded at `MAX_CATALOG_PAGE_SIZE`, which is the top of
 * that menu and so the largest size any click can reach.
 *
 * `z.coerce.number()` because a hand-typed `?pageSize=50` may arrive as a
 * string: TanStack's default search parser reads JSON-ish values, so a param
 * copied from elsewhere can land either way. Coercing here means both spellings
 * are the same page size rather than one of them being silently dropped — and
 * the `.pipe` is what carries the bound through the coercion.
 */
const urlPageSize = urlNumber.pipe(catalogPageSize);

const urlPage = urlNumber.pipe(z.number().int().min(1).max(MAX_CATALOG_PAGE));

/**
 * A category slug, read from a URL — the donor's own spelling
 * (`CategoryFilter.tsx`: `params.set(CATEGORY, JSON.parse(category).slug)`), so
 * a Spec Tool link keeps working here.
 *
 * `z.coerce.string()`, because a slug can be all digits (`"2024"`) and the
 * router JSON-parses each param, handing that one over as a number. Bounded to
 * the column's own width, and to the characters `slugify` can produce, so a
 * hand-edited param is dropped here rather than travelling to Python as a
 * filter nothing can match.
 *
 * The slug is resolved to a subtree by the *list use case*, not by the browser:
 * resolving it here would make the list request wait on the category-tree read
 * before it could ask for a page.
 */
const urlCategorySlug = z.coerce
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(CATEGORY_SLUG_MAX_LENGTH);

/**
 * The free-text search term, as a URL may spell it.
 *
 * Plainly `z.string()`, unlike the category slug beside it, because the router
 * hands this one over as the literal text the URL spells — `search` is a
 * free-text param in `@/lib/router-search-codec`, so it never arrives as the
 * number or object JSON would have made of it. That is the donor's reading:
 * `items-list-page.tsx` writes and reads its term with plain `URLSearchParams`,
 * so `?search=95805021` is the model number a user typed and `?search=1.2300`
 * keeps its trailing zeroes.
 *
 * Capped at Python's own `SEARCH_MAX_LENGTH`, and **dropped** past it rather
 * than clamped. Python refuses a longer term outright and that refusal reaches
 * the browser as the generic scrubbed message, so a hand-edited URL falls back
 * to a plain browse — the rule every other field here follows. Truncating
 * instead would silently search for something the user did not type.
 *
 * Not trimmed here. "An empty or whitespace-only box behaves exactly like no
 * search" is one rule with one home — `normalizeCatalogSearchTerm` — and the
 * page applies it to this value before anything reads it. A schema that also
 * trimmed would make the rule true in two places and provable in neither.
 *
 * The *cap* is measured the way Python measures it, though — see
 * `searchTermLength`. Two sides disagreeing about what "200 characters" counts
 * is how a term the server would have accepted gets dropped before it is sent.
 */
const urlSearchTerm = z
  .string()
  .refine((value) => catalogSearchTermLength(value) <= CATALOG_SEARCH_TERM_MAX_LENGTH);

/**
 * One of the three positions a radio filter can be in, as a URL may spell it.
 *
 * **Boolean or string**, because the router's serializer is JSON per value
 * (`defaultParseSearch` = `parseSearchWith(JSON.parse)`): `?filterEI=true` —
 * the donor's own spelling, and what this app writes — arrives here as the
 * *boolean* `true`, while `?filterEI=all` arrives as the string `"all"` (it is
 * not JSON). Accepting only strings would drop every applied filter on the way
 * in, and writing one would put `%22true%22` in the address bar. Normalising to
 * the tristate string is `searchToFilters`' job.
 */
const urlTristate = z.union([z.boolean(), z.enum(TRISTATE_FILTER_VALUES)]);

/**
 * The list route's search params.
 *
 * **Every field drops to `undefined` rather than failing.** The URL is
 * user-editable and back-button traffic replays whatever was there, so a
 * schema that threw would turn a stale bookmark into a broken navigation. Each
 * `.catch(undefined)` is scoped to its own field, which is what keeps one bad
 * param from taking its readable neighbours down with it.
 *
 * **No field carries a default**, so absence resolves to the default and an
 * explicit default resolves to the same thing — which is what lets a fresh
 * visit stay a bare address while a touched control spells its value out.
 *
 * Unknown keys pass over — the object is deliberately not strict.
 *
 * What the page *writes* into these params splits in two, and both classes are
 * the donor's (`items-list-page.tsx`'s handlers; see `filterChangeToSearch`):
 *
 *   - **Explicit once touched** — `page`, `pageSize`, `view`, the four
 *     tristates, `showDeleted`. Their donor handlers `.set()` unconditionally,
 *     so `?page=1`, `?pageSize=20`, `?view=card`, `?filterEI=all` and
 *     `?showDeleted=false` are all values this page writes, not merely ones it
 *     reads. Each therefore has to be *nameable* here: `showDeleted` accepts
 *     `false` and `"false"` for exactly that reason, and the tristates already
 *     accepted `"all"`.
 *   - **Delete when empty** — `search`, `sort`, `category`, `mfrs`, `tiers`. Their donor
 *     handlers `.delete()`, so these params are absent rather than empty.
 *
 * (This replaces an earlier "absent means absent" rule, under which every
 * control dropped its param at the default. That was a URL shape decided at
 * implementation time without checking the donor's writes, and #366 ruled it
 * back to donor parity — a link copied out of Atlas has to be the same string
 * the Spec Tool would hand you for the same clicks.)
 */
export const catalogItemsSearchSchema = z.object({
  view: z.enum(CATALOG_VIEWS).optional().catch(undefined),
  page: urlPage.optional().catch(undefined),
  pageSize: urlPageSize.optional().catch(undefined),
  // The donor's four `order_by` spellings, not Atlas's wire vocabulary: this is
  // the value a person copies out of one stack and pastes into the other
  // (`catalogSortFromUrl`). An unknown value drops to Recommended, as it does
  // there.
  sort: z.enum(CATALOG_SORT_URL_VALUES).optional().catch(undefined),
  search: urlSearchTerm.optional().catch(undefined),
  category: urlCategorySlug.optional().catch(undefined),
  // A number as well as a string, for the same JSON-per-value reason: a single
  // `?mfrs=7` arrives as the number 7, while `?mfrs=7 3` is not JSON and stays
  // a string.
  mfrs: z.union([z.string(), z.number()]).optional().catch(undefined),
  // The EI Package filter's tier ids, spelled as `mfrs` is (`?tiers=14 3`) —
  // the same shape the spec editor's item search reads, so one link reads in
  // both.
  tiers: z.union([z.string(), z.number()]).optional().catch(undefined),
  filterEI: urlTristate.optional().catch(undefined),
  filterCore: urlTristate.optional().catch(undefined),
  filterApproved: urlTristate.optional().catch(undefined),
  filterRecommendationStatus: urlTristate.optional().catch(undefined),
  // Both spellings of both ends. The router JSON-parses each param, so the
  // `?showDeleted=false` this page now writes arrives as the *boolean* `false`;
  // the quoted forms are what a hand-edited or re-encoded link can carry.
  showDeleted: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .optional()
    .catch(undefined),
});

export type CatalogItemsSearch = z.infer<typeof catalogItemsSearchSchema>;

/**
 * The server function's input — the list state and its filters, resolved.
 *
 * Strict and unforgiving where the search schema is lenient, because the two
 * answer different questions. By the time the hook calls, the defaults are
 * applied and the values came from the page's own controls: an out-of-range
 * page size here is a bug in this codebase, not a user editing a URL, and
 * clamping it would hide that.
 *
 * `strictObject` refuses a key this seam does not declare. The list endpoint's
 * wire face is strict for the same reason, and refusing `search` here reports
 * it as invalid input rather than as a contract error deeper in the call.
 *
 * **Every filter is optional, and absent is the only spelling of "no filter".**
 * The wire encoder sends exactly the keys it is given, so a call that named no
 * filters puts no filter set on the wire — which is why `null` is refused here
 * rather than accepted as a synonym for absent: two spellings of one state
 * would also be two react-query keys for one result set.
 *
 * `manufacturerIds` refuses an **empty** array for a sharper reason: Python
 * reads a present-but-empty list as a filter, so `[]` would return nothing at
 * all. No manufacturers selected means the key is absent.
 */
export const listCatalogItemsInputSchema = z.strictObject({
  page: z.number().int().min(1).max(MAX_CATALOG_PAGE),
  pageSize: catalogPageSize,
  sort: z.enum(CATALOG_ITEM_SORTS),
  /**
   * The term as typed, trimmed — never `""`.
   *
   * Absent is the only spelling of "no term", matching every filter below it:
   * `""` would be a second URL, a second react-query key and a second request
   * for one browse view. The page trims before it gets here, and sends the
   * result unmodified from here on — Python owns the normalisation the ranking
   * read runs on, and a term trimmed twice is a rule provable in neither place.
   *
   * With a term present Python ignores `sort` and ranks by relevance, which is
   * the source's rule; the toolbar says so by going inert rather than by
   * dropping the value the URL still carries.
   */
  // Already normalised by the time it reaches here — the page trims before it
  // asks — so this is the same cap as the URL's, measured the same way.
  search: z
    .string()
    .min(1)
    .refine((value) => catalogSearchTermLength(value) <= CATALOG_SEARCH_TERM_MAX_LENGTH)
    .optional(),
  categorySlug: z.string().min(1).optional(),
  manufacturerIds: z.array(z.number().int().min(1)).min(1).optional(),
  /**
   * The Item Search island's tier filter (#811): any of these tiers. Refuses
   * an empty list for `manufacturerIds`' reason.
   */
  tierIds: z.array(z.number().int().min(1)).min(1).optional(),
  /** The island's pinned-item read (#811): this one item, under every other filter. */
  itemId: z.number().int().min(1).optional(),
  hasEiTiers: z.boolean().optional(),
  isCore: z.boolean().optional(),
  isApproved: z.boolean().optional(),
  isNotRecommended: z.boolean().optional(),
  includeDeleted: z.boolean().optional(),
  /**
   * Each row's whole gallery beside the page — the Item Search island's rows,
   * whose CTA dialog draws its thumbnail strip on its first frame (#964 N16).
   */
  includeImages: z.boolean().optional(),
});

/**
 * The browser seam's input. Named for its seam, because the *wire* face has an
 * input type of its own (`ListCatalogItemsWireInput`, on the `endpoint()`
 * declaration) with a different shape — every field optional, and each filter
 * nullable as well as absent. `handleList` widens one into the other,
 * and `catalog-wire-drift.unit.test.ts` pins that it still may.
 */
export type ListCatalogItemsBrowserInput = z.infer<typeof listCatalogItemsInputSchema>;

/**
 * The Request Item dialog's input, browser seam.
 *
 * A **second** face over the same fields as the create endpoint's wire face
 * (`itemCreateRequestWire`, on the declaration), and deliberately not shared
 * with it: this one describes what the *browser* may send the BFF, and the two
 * seams have two homes (`client/docs/architecture.md` L2). They are pinned
 * together by `catalog-wire-drift.unit.test.ts`.
 *
 * Strict, and every optional field value-or-absent rather than nullable: the
 * dialog builds its payload with `createInputFromDraft`
 * (`@/lib/request-item-form`), which leaves a blank field out entirely. A `null`
 * arriving here would mean a caller that is not the dialog.
 *
 * The four required fields are shape, not validation. The dialog refuses to
 * submit without them and says which one is missing — a rejection here is
 * scrubbed to a generic message by the global error middleware, so it can only
 * ever report a bug in this codebase, never explain anything to a typist.
 */
export const requestCatalogItemInputSchema = z.strictObject({
  name: z.string(),
  manufacturerId: z.number().int(),
  categoryId: z.number().int(),
  finishId: z.number().int(),
  materialId: z.number().int().optional(),
  modelNumber: z.string().optional(),
  jdeItemNumber: z.string().optional(),
  propId: z.string().optional(),
  url: z.string().optional(),
  description: z.string().optional(),
  dimensionWidth: z.string().optional(),
  dimensionHeight: z.string().optional(),
  dimensionDepth: z.string().optional(),
  // Ids of catalog images the dialog's uploader already put in the bucket, in
  // the order the field shows them — the first one is the hero.
  imageIds: z.array(z.number().int()).optional(),
});

export type RequestCatalogItemBrowserInput = z.infer<typeof requestCatalogItemInputSchema>;

/**
 * The Add Variant dialog's input, browser seam.
 *
 * A second face over `itemAddVariantRequestWire` — the same two-seams rule as
 * the create input above (`client/docs/architecture.md` L2), pinned to it by
 * `catalog-wire-drift.unit.test.ts`.
 *
 * **`itemId` is the parent item's, and it is the only field here that does not
 * reach the body.** The wire declaration takes path parameters and body fields
 * in one object and its `path()` picks `itemId` out
 * (`/internal/catalog/items/{itemId}/variants`), so the two travel together on
 * this side and separate at the declaration.
 *
 * **No `manufacturerId` and no `categoryId`**, deliberately: the variant group
 * owns both and Python derives them from the parent (ADR-0005 decision 13). The
 * dialog shows them locked, and shows them from the parent it already has —
 * nothing is sent, so there is nothing here to send it with. `strictObject` is
 * what makes a future caller's attempt a refusal rather than a silent drop.
 *
 * No `propId` either, and that one is the *form's* difference rather than the
 * wire's: the donor's variant form has no Prop ID control
 * (`AddVariantIsland.tsx`), though the column exists and the edit form writes
 * it.
 */
export const addCatalogItemVariantInputSchema = z.strictObject({
  itemId: z.number().int().positive(),
  name: z.string(),
  finishId: z.number().int(),
  materialId: z.number().int().optional(),
  modelNumber: z.string().optional(),
  jdeItemNumber: z.string().optional(),
  url: z.string().optional(),
  description: z.string().optional(),
  dimensionWidth: z.string().optional(),
  dimensionHeight: z.string().optional(),
  dimensionDepth: z.string().optional(),
});

export type AddCatalogItemVariantBrowserInput = z.infer<typeof addCatalogItemVariantInputSchema>;

/**
 * One item, addressed by id — the details page's read, and its delete.
 *
 * The same shape for both, because both say the same thing: *which* item. It is
 * a strict object rather than a bare number so the two server functions have a
 * validator at all — Start hands `data` to the handler untouched otherwise, and
 * an id is the one input on this surface that can arrive from a URL a user
 * typed.
 *
 * `.int().positive()` rather than a plain number: the path segment is
 * interpolated into `/internal/catalog/items/{id}`, and a fractional or
 * negative id addresses no row on either side of the wire. A rejection here is
 * scrubbed to the generic message like every validator on this seam, which is
 * right — the route parses the id out of the path itself and renders its own
 * 404 for one that does not resolve, so a rejection at this seam can only be a
 * caller that is not the route.
 */
export const catalogItemByIdInputSchema = z.strictObject({
  itemId: z.number().int().positive(),
});

export type CatalogItemByIdBrowserInput = z.infer<typeof catalogItemByIdInputSchema>;

/**
 * The item edit page's save — an id, and only the fields the editor moved.
 *
 * **Every field is optional, and a key's presence is the instruction**: absent
 * leaves the column alone, `null` clears it, a value sets it. That is the update
 * wire's own three-state shape (`itemUpdateRequestWire`), and this seam keeps it
 * rather than flattening it, because the form's whole submit rule is that it
 * sends a *diff* (`@/lib/edit-item-form`'s `updateInputFromDraft`).
 *
 * Which fields may be `null` is not uniform, and the asymmetry is the source's:
 *
 *   - The four **required** controls — name, manufacturer, category,
 *     color/finish — are value-or-absent. The form cannot submit with one of
 *     them empty, so a `null` arriving here could only be a caller that is not
 *     the form, and refusing it at the seam is cheaper than discovering it as a
 *     Python-side integrity error.
 *   - Material and the seven optional text fields are value-or-null-or-absent,
 *     because clearing them is something the screen can do.
 *   - The four **Internal Use Only** fields are value-or-absent. They are
 *     booleans and a tier list; there is no "clear" state for a checkbox, and
 *     `tierIds: []` — not `null` — is how the screen says "no tiers". Refusing
 *     the `null` here keeps the one wire state this form has no control for out
 *     of reach.
 *
 * `images` is **here, and it is still the one field that destroys rows** — every
 * image the list omits is deleted. The edit page now hosts the upload section
 * (#300), so the seam has to be able to spell it; what took over as the guard is
 * `imageListPatch` (`@/lib/edit-item-form`), which emits the key only when the
 * strip actually differs from the item's own list, so an ordinary save of a text
 * field cannot reach the deleting field at all. That guard is a *diff*, not a
 * lock: the strip is seeded once while the list it is compared against follows
 * the details cache, so an image added elsewhere between opening the form and
 * pressing Save is deleted by that Save. Last write wins, exactly as it does for
 * every other field on this form and in the donor, whose baseline is likewise
 * the live query (`useEditProduct.ts:158`). `null` is *not* admitted, unlike
 * the wire face: there it is a documented no-op, and a second spelling of
 * "leave the images alone" is one the form has no control for.
 */
export const updateCatalogItemInputSchema = z.strictObject({
  itemId: z.number().int().positive(),
  name: z.string().optional(),
  manufacturerId: z.number().int().optional(),
  categoryId: z.number().int().optional(),
  finishId: z.number().int().optional(),
  materialId: z.number().int().nullable().optional(),
  modelNumber: z.string().nullable().optional(),
  jdeItemNumber: z.string().nullable().optional(),
  propId: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  dimensionWidth: z.string().nullable().optional(),
  dimensionHeight: z.string().nullable().optional(),
  dimensionDepth: z.string().nullable().optional(),
  isCore: z.boolean().optional(),
  isNotRecommended: z.boolean().optional(),
  isApproved: z.boolean().optional(),
  tierIds: z.array(z.number().int()).optional(),
  // The item's whole image set, in display order: an id per row and where it
  // sits. `orderIndex` is a *ranking* — Python sorts by it and renumbers the set
  // `1..n` — so the form counts from its display position and the hero falls out
  // of being first (ADR-0005 decision 9, no `is_hero` column). `[]` is a real
  // instruction ("this item has no images") rather than an empty-ish value to
  // drop, which is why the emptiness rule lives in `imageListPatch` and not
  // here.
  images: z
    .array(z.strictObject({ id: z.number().int(), orderIndex: z.number().int() }))
    .optional(),
});

export type UpdateCatalogItemBrowserInput = z.infer<typeof updateCatalogItemInputSchema>;
